import { PaperclipIcon } from 'lucide-react'

import { cn } from '@/utils'
import type { PendingMessage } from '../optimisticMessages'
import { LinkedText } from './LinkedText'

interface PendingMessageBubbleProps {
  pending: PendingMessage
  onRetry: (pending: PendingMessage) => void
  onDiscard: (pending: PendingMessage) => void
}

const FailedSendActions = ({
  pending,
  onRetry,
  onDiscard,
}: PendingMessageBubbleProps) => (
  <div className="mt-2 flex items-center gap-3 text-xs">
    <span className="font-medium text-red-700">Not sent</span>
    <button
      type="button"
      onClick={() => onRetry(pending)}
      className="cursor-pointer font-medium text-red-700 underline underline-offset-2 hover:text-red-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      Retry
    </button>
    <button
      type="button"
      onClick={() => onDiscard(pending)}
      className="cursor-pointer text-red-700 underline underline-offset-2 hover:text-red-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      Discard
    </button>
  </div>
)

// The composer clears the moment a reply is handed over, so while the upload is
// still running this is the only place the attachments are still named.
const AttachmentNames = ({ files }: { files: File[] }) => {
  if (files.length === 0) return null

  return (
    <div className="mt-2 space-y-1">
      {files.map((file) => (
        <span
          key={`${file.name}-${file.size}-${file.lastModified}`}
          className="flex items-center gap-1.5 text-xs"
        >
          <PaperclipIcon className="h-3 w-3 shrink-0" />
          <span className="truncate">{file.name}</span>
        </span>
      ))}
    </div>
  )
}

// A send in flight keeps the full bubble colour: fading it took the white text
// below the readable floor, and the "Sending" line says the same thing.
export const PendingMessageBubble = ({
  pending,
  onRetry,
  onDiscard,
}: PendingMessageBubbleProps) => (
  <div
    className={cn(
      'max-w-full rounded-2xl px-4 py-3 text-sm leading-relaxed',
      pending.failed
        ? 'border border-red-200 bg-red-50 text-red-900'
        : 'bg-brand-deep text-white',
    )}
  >
    <p className="whitespace-pre-wrap break-words">
      <LinkedText text={pending.body} />
    </p>
    <AttachmentNames files={pending.files} />

    {pending.failed ? (
      <FailedSendActions
        pending={pending}
        onRetry={onRetry}
        onDiscard={onDiscard}
      />
    ) : (
      <p className="mt-1 text-xs text-white/90">Sending</p>
    )}
  </div>
)
